import { Injectable } from '@angular/core';
import {
    BehaviorSubject, EMPTY, Observable, Subject, Subscription,
    auditTime, catchError, combineLatest, distinctUntilChanged,
    filter, finalize, interval, map, shareReplay, skip, switchMap, take, tap,
} from 'rxjs';
import { ApiService } from '@core/services/api.service';
import { LoadingService } from '@core/services/loading.service';
import { ToastMsgService } from '@core/services/toast-msg.service';
import { SignalRService } from '@shared/services/signalr.service';
import { UserDataService } from '@user/services/user-data.service';
import { ApiConfig } from '@shared/config/api.config';
import { DRAFT_HEADLINE, signalRConfig } from '@app/app.constants';
import { buildCreateNewsPayload } from '@shared/utils/common.utils';
import { NewsItemInterface } from '@shared/models/news-item.interface';
import { DocumentInterface } from '@shared/models/document.interface';
import { CompanyInterface } from '@shared/models/company.interface';
import { TaxonomyFieldsEnum } from '@shared/models/taxonomy';
import { SubjectItemInterface } from '@shared/models/subject.interface';
import { NewsSummaryInterface, NewsSummaryOutputInterface } from '@shared/models/news-summary.interface';
import { NewsSummaryTypeEnum } from '@shared/models/news-summary-type.enum';
import { NdFreshCount, NewsDataInterface, NewsListInterface } from '../models/news-desk.models';

@Injectable({ providedIn: 'root' })
export class NewsDeskService {

    private readonly _refresh$ = new Subject<void>();
    private readonly _detailRequest$ = new Subject<number>();
    private readonly _pollInterval = 15_000;
    private readonly _loadRetryDelay = 5_000;

    readonly newItemsAway$ = new Subject<number>();
    readonly allNews$ = new BehaviorSubject<NewsListInterface>({} as NewsListInterface);
    readonly currentItem$ = new BehaviorSubject<NewsItemInterface | null>(null);
    readonly currentItemDetails$ = new BehaviorSubject<NewsDataInterface | null>(null);
    readonly isFlashMode$ = new BehaviorSubject<boolean>(false);
    readonly isInitializing$ = new BehaviorSubject<boolean>(false);
    readonly activeSummaryPublished$ = new BehaviorSubject<boolean>(false);

    private _isListening = false;
    private _isFirstLoad = true;
    private _pollingSub: Subscription | null = null;
    private _signalRSub: Subscription | null = null;
    private _loadRetryTimer: ReturnType<typeof setTimeout> | null = null;
    private _connected = false;
    private _refreshing = false;
    private _refreshQueued = false;
    private readonly _subjectsByProfile = new Map<string, Observable<{ subjects: SubjectItemInterface[] }>>();
    private _textTypes$: Observable<string[]> | null = null;
    private _profiles$: Observable<string[]> | null = null;

    freshNewsCount: NdFreshCount = { full_intake_news_items: 0, edited_news_items: 0, published_news_items: 0 };
    lastUpdated = '';

    constructor(
        private readonly _api: ApiService,
        private readonly _signalR: SignalRService,
        private readonly _loading: LoadingService,
        private readonly _toast: ToastMsgService,
        private readonly _user: UserDataService,
    ) {
        // switchMap cancels the previous in-flight details request when a new item is
        // selected, so rapid list clicks never leave stale calls running or apply
        // out-of-order responses. catchError keeps the long-lived stream alive on failure.
        this._detailRequest$.pipe(
            switchMap(id =>
                this._api.call(ApiConfig.endpoints.newsItem, { news_id: id }).pipe(
                    catchError(() => { this._toast.showError('Cannot get news details'); return EMPTY; }),
                )
            ),
        ).subscribe(details => this.currentItemDetails$.next(details));
    }

    get currentDetails(): NewsDataInterface | null {
        return this.currentItemDetails$.getValue();
    }

    loadNews(): void {
        if (this.allNews$.getValue().full_intake_news_items?.length) {
            this._startListening();
            return;
        }

        if (this._loadRetryTimer) return;
        this.isInitializing$.next(true);

        const profile$ = this._user.userProfile.pipe(
            filter(u => !!u?.profile),
            take(1),
        );

        combineLatest([this._api.call(ApiConfig.endpoints.news), profile$])
            .pipe(take(1))
            .subscribe({
                next: ([newsData]) => {
                    newsData = { ...newsData, full_intake_news_items: this._uniqueByNewsId(newsData.full_intake_news_items ?? []).sort((a, b) => this._newestFirst(a, b)) };
                    this.allNews$.next(newsData);
                    this.lastUpdated = this._toTimestamp(newsData.last_updated);
                    if (this._isFirstLoad) {
                        this._setInitialCounts(newsData.full_intake_news_items);
                        this._isFirstLoad = false;
                    }
                    this.isInitializing$.next(false);
                    this._startListening();
                },
                // Without this the spinner would hang forever, and nothing would ever load or listen.
                error: () => {
                    this.isInitializing$.next(false);
                    this._loadRetryTimer = setTimeout(() => {
                        this._loadRetryTimer = null;
                        this.loadNews();
                    }, this._loadRetryDelay);
                },
            });
    }

    refreshNews(): void {
        this._refresh$.next();
    }

    selectItem(item: NewsItemInterface): void {
        this.currentItem$.next(item);
        this.currentItemDetails$.next({} as NewsDataInterface);
        // A row that reached the list without an id would otherwise ask for
        // news_item?news_id=undefined and blank the panel on the error.
        if (item.news_id != null) this._fetchDetails(item.news_id);
    }

    clearSelection(): void {
        this.currentItem$.next(null);
        this.currentItemDetails$.next(null);
    }

    refreshDetails(): void {
        const id = this.currentDetails?.news_item?.news_id;
        if (id) this._fetchDetails(id);
    }

    patchCurrentItem(updates: Partial<NewsItemInterface>): void {
        const item = this.currentItem$.getValue();
        if (item) this.currentItem$.next({ ...item, ...updates });
    }

    patchListItem(newsId: number, updates: Partial<NewsItemInterface>): void {
        const current = this.allNews$.getValue();
        if (!current.full_intake_news_items) return;
        const items = current.full_intake_news_items.map(i =>
            i.news_id === newsId ? { ...i, ...updates } : i
        );
        this.allNews$.next({ ...current, full_intake_news_items: items });
    }

    createNews(headline: string, language: string): Observable<any> {
        return this._api.call(ApiConfig.endpoints.createNews, buildCreateNewsPayload(headline, '', language)).pipe(
            tap(() => this.refreshNews())
        );
    }

    updateNewsContent(newsId: number, docId: number, headline: string, content: string): Observable<any> {
        return this._api.call(ApiConfig.endpoints.updateNews, { news_id: newsId, doc_id: docId, headline, content }).pipe(
            tap(() => {
                this.patchListItem(newsId, { headline });
                this.patchCurrentItem({ headline });
                this.patchCurrentDocument({ doc_content: content });
            })
        );
    }

    patchCurrentDocument(updates: Partial<DocumentInterface>): void {
        const details = this.currentItemDetails$.getValue();
        if (!details?.main_document) return;
        this.currentItemDetails$.next({ ...details, main_document: { ...details.main_document, ...updates } });
    }

    updateLanguage(newsId: number, docId: number, language: string): Observable<any> {
        const item = this.currentItem$.getValue();
        const content = this.currentDetails?.main_document?.doc_content ?? '';
        return this._api.call(ApiConfig.endpoints.updateNews, { news_id: newsId, doc_id: docId, headline: item?.headline ?? '', content, language }).pipe(
            tap(() => {
                this.patchCurrentItem({ langauge: language });
                this.patchListItem(newsId, { langauge: language });
                this._patchCurrentNewsItem({ langauge: language });
            })
        );
    }

    searchCompanies(query: string): Observable<CompanyInterface[]> {
        return this._api.call(ApiConfig.endpoints.getCompanyName, { name: query }).pipe(
            map((r: any) => Array.isArray(r) ? r : [])
        );
    }

    saveCompanies(docId: number, companies: CompanyInterface[]): Observable<any> {
        return this._api.call(ApiConfig.endpoints.saveCompanies, companies.map(c => c.company_id), { doc_id: docId }).pipe(
            tap(() => {
                this._patchCurrentNewsItem({ companies });
                const newsId = this.currentItem$.getValue()?.news_id;
                if (newsId) this.patchListItem(newsId, { companies });
            })
        );
    }

    // Without a profile this follows the user's own: it waits for it to load and switches
    // lists when they change it, so it never caches a list fetched for an empty profile.
    loadSubjects(profile?: string): Observable<{ subjects: SubjectItemInterface[] }> {
        if (profile) return this._subjectsFor(profile);
        return this._user.userProfile.pipe(
            map(u => u?.profile),
            filter((p): p is string => !!p),
            distinctUntilChanged(),
            switchMap(p => this._subjectsFor(p)),
        );
    }

    private _subjectsFor(profile: string): Observable<{ subjects: SubjectItemInterface[] }> {
        let subjects$ = this._subjectsByProfile.get(profile);
        if (!subjects$) {
            subjects$ = this._api.call(ApiConfig.endpoints.getSubjects, { category_id: -1, profile }).pipe(shareReplay(1));
            this._subjectsByProfile.set(profile, subjects$);
        }
        return subjects$;
    }

    loadTextTypes(): Observable<string[]> {
        if (!this._textTypes$) {
            this._textTypes$ = (this._api.call(ApiConfig.endpoints.getTextTypes) as Observable<any>).pipe(
                map((r: any) => Array.isArray(r) ? r : (r?.text_types ?? [])),
                shareReplay(1),
            );
        }
        return this._textTypes$;
    }

    loadProfiles(): Observable<string[]> {
        if (!this._profiles$) {
            this._profiles$ = (this._api.call(ApiConfig.endpoints.getProfiles) as Observable<any>).pipe(
                map((r: any) => (r?.profiles ?? []).map((p: any) => p.profile_name as string)),
                shareReplay(1),
            );
        }
        return this._profiles$;
    }

    saveSubjects(newsId: number, subjects: string[]): Observable<any> {
        return this._api.call(ApiConfig.endpoints.saveSubjects, subjects, { news_id: newsId }).pipe(
            tap(() => {
                this._patchCurrentNewsItem({ subjects });
                this.patchListItem(newsId, { subjects });
            })
        );
    }

    saveTopics(docId: number, topics: TaxonomyFieldsEnum[]): Observable<any> {
        return this._api.call(ApiConfig.endpoints.saveTopics, topics, { doc_id: docId }).pipe(
            tap(() => {
                const item = this.currentItem$.getValue();
                if (item) {
                    this.patchCurrentItem({ topics });
                    this.patchListItem(item.news_id, { topics });
                }
            })
        );
    }

    private _patchCurrentNewsItem(updates: Partial<NewsItemInterface>): void {
        const details = this.currentItemDetails$.getValue();
        if (!details?.news_item) return;
        this.currentItemDetails$.next({ ...details, news_item: { ...details.news_item, ...updates } });
    }

    saveSummary(summary: NewsSummaryInterface, publish = false): Observable<any> {
        const payload = this._buildSummaryPayload(summary);
        const qParams: Record<string, any> = {};
        if (summary.summary_id) qParams['summary_id'] = summary.summary_id;
        if (publish) qParams['publish'] = true;

        this._loading.start();
        return this._api.call(
            ApiConfig.endpoints.summary,
            payload,
            Object.keys(qParams).length ? qParams : undefined,
        ).pipe(finalize(() => this._loading.stop()));
    }

    removeSummary(summaryId: number): void {
        const details = this.currentItemDetails$.getValue();
        if (!details) return;
        const summaries = (details.news_summaries ?? []).filter(s => s.summary_id !== summaryId);
        this.currentItemDetails$.next({ ...details, news_summaries: summaries });
    }

    updateSummaryStatus(isFirstSummary: boolean, isPublish: boolean): void {
        const item = this.currentItem$.getValue();
        if (!item) return;

        const wasEdited = !!item.has_summary_prediction && !item.has_summary_published;
        const wasPublished = !!item.has_summary_published;
        const nowHasSummary = isFirstSummary || !!item.has_summary_prediction;
        const nowPublished = isPublish || wasPublished;
        const nowEdited = nowHasSummary && !nowPublished;

        if (nowHasSummary !== !!item.has_summary_prediction || nowPublished !== wasPublished) {
            const updates = { has_summary_prediction: nowHasSummary, has_summary_published: nowPublished };
            this.patchCurrentItem(updates);
            this.patchListItem(item.news_id, updates);
            this._patchCurrentNewsItem(updates);
        }

        if (!wasEdited && nowEdited) this.freshNewsCount.edited_news_items++;
        if (wasEdited && !nowEdited) this.freshNewsCount.edited_news_items = Math.max(0, this.freshNewsCount.edited_news_items - 1);
        if (!wasPublished && nowPublished) this.freshNewsCount.published_news_items++;
    }

    upsertSummary(summary: NewsSummaryInterface): void {
        const details = this.currentItemDetails$.getValue();
        if (!details) return;
        const id = +summary.summary_id;
        const exists = (details.news_summaries ?? []).some(s => +s.summary_id === id);
        const summaries = exists
            ? (details.news_summaries ?? []).map(s => +s.summary_id === id ? summary : s)
            : [...(details.news_summaries ?? []), summary];
        this.currentItemDetails$.next({ ...details, news_summaries: summaries });
    }

    deleteSummary(summaryId: number): Observable<any> {
        this._loading.start();
        return this._api.call(ApiConfig.endpoints.deleteSummary, { summary_id: summaryId }).pipe(
            finalize(() => this._loading.stop())
        );
    }

    autosaveSummary(summary: NewsSummaryInterface): Observable<any> {
        const payload = this._buildSummaryPayload(summary);
        payload.headline = payload.headline || DRAFT_HEADLINE;
        const qParams: Record<string, any> = {};
        if (summary.summary_id) qParams['summary_id'] = summary.summary_id;
        return this._api.call(
            ApiConfig.endpoints.summary,
            payload,
            Object.keys(qParams).length ? qParams : undefined,
        );
    }

    // Kept as the existing GET with paragraph and word counts until the backend is ready. The next
    // step switches it to a POST carrying the selected AI prompt text (see commit 32524ce9).
    getAiSummary(
        docId: number,
        model: string,
        language: string,
        paragraphs: number,
        words: number,
        positions: string,
        isFlash: boolean,
    ): Observable<any> {
        return this._api.call(ApiConfig.endpoints.aiSummary, {
            doc_id: docId,
            ai_model: model,
            ai_language: language,
            paragraphs,
            words,
            positions,
            is_flash: isFlash,
        }).pipe(
            tap(() => {
                const item = this.currentItem$.getValue();
                if (item && !item.has_summary_prediction) {
                    this.updateSummaryStatus(true, false);
                }
            })
        );
    }

    private _fetchDetails(id: number): void {
        this._detailRequest$.next(id);
    }

    private _startListening(): void {
        if (this._isListening) return;
        this._isListening = true;

        this._refresh$.subscribe(() => this._runRefresh());

        this._signalR.signalRConnectionStatus.pipe(take(1)).subscribe(connected => {
            this._connected = connected;
            this._toggleSignalR(connected);
            this._togglePolling(!connected);
        });

        // Broadcasts that arrived while the first list was loading had no listener yet.
        this.refreshNews();

        // React to subsequent connection changes only (skip the initial BehaviorSubject emit).
        // A transition TO connected means we may have missed events → do a catch-up refresh.
        this._signalR.signalRConnectionStatus.pipe(distinctUntilChanged(), skip(1)).subscribe(connected => {
            this._connected = connected;
            this._toggleSignalR(connected);
            this._togglePolling(!connected);
            if (connected) setTimeout(() => this.refreshNews(), 2000);
        });
    }

    // One request at a time: a refresh asked for mid-flight runs right after it. Cancelling the
    // request in flight instead would starve the list whenever events outpace the API.
    private _runRefresh(): void {
        if (this._refreshing) {
            this._refreshQueued = true;
            return;
        }
        this._refreshing = true;
        this._api.call(ApiConfig.endpoints.news, { last_updated: this._adjustedLastUpdated() }).pipe(
            finalize(() => {
                this._refreshing = false;
                if (this._refreshQueued) {
                    this._refreshQueued = false;
                    this._runRefresh();
                }
            }),
        ).subscribe({
            next: fresh => this._applyRefresh(fresh),
            // While connected, no broadcast would retry a failed refresh, so poll until one succeeds.
            error: () => this._togglePolling(true),
        });
    }

    private _applyRefresh(fresh: NewsListInterface): void {
        if (this._connected) this._togglePolling(false);
        const items: NewsItemInterface[] = fresh.full_intake_news_items ?? [];
        if (items.length) {
            this.lastUpdated = fresh.last_updated ? this._toTimestamp(fresh.last_updated) : this._latestDate(items);
            this._mergeNews(fresh);
        }
    }

    private _mergeNews(fresh: NewsListInterface): void {
        const current = this.allNews$.getValue();
        const currentItems = current.full_intake_news_items ?? [];
        const incomingItems = this._uniqueByNewsId(fresh.full_intake_news_items ?? []);

        const currentIds = new Set(currentItems.map(i => i.news_id));

        const newItems = incomingItems.filter(i => !currentIds.has(i.news_id));
        const updatedItems = incomingItems.filter(i => currentIds.has(i.news_id));

        if (!newItems.length && !updatedItems.length) return;

        const updatedMap = new Map(updatedItems.map(i => [i.news_id, i]));
        const merged = [
            ...newItems,
            ...currentItems.map(i => updatedMap.get(i.news_id) || i)
        ].sort((a, b) => this._newestFirst(a, b));

        const prevEdited = currentItems.filter(i => i.has_summary_prediction && !i.has_summary_published).length;
        const prevPublished = currentItems.filter(i => i.has_summary_published).length;
        const nextEdited = merged.filter(i => i.has_summary_prediction && !i.has_summary_published).length;
        const nextPublished = merged.filter(i => i.has_summary_published).length;

        this.freshNewsCount.full_intake_news_items += newItems.length;
        this.freshNewsCount.edited_news_items = Math.max(0, this.freshNewsCount.edited_news_items + (nextEdited - prevEdited));
        this.freshNewsCount.published_news_items = Math.max(0, this.freshNewsCount.published_news_items + (nextPublished - prevPublished));
        if (newItems.length > 0) this.newItemsAway$.next(newItems.length);

        const companies = fresh.full_companies_list?.length
            ? [...fresh.full_companies_list, ...(current.full_companies_list ?? [])]
            : (current.full_companies_list ?? []);

        this.allNews$.next({ ...current, full_intake_news_items: merged, full_companies_list: companies });
    }

    // Newest first to the second, even though the list only shows HH:mm. news_id follows arrival
    // order, which can differ from the item time; it only breaks ties within the same second.
    private _newestFirst(a: NewsItemInterface, b: NewsItemInterface): number {
        return this._toTimestamp(b.dtg).localeCompare(this._toTimestamp(a.dtg)) || b.news_id - a.news_id;
    }

    // The backend can return the same news_id more than once (one row per language variant).
    // Everything downstream is keyed on news_id, so keep the first row and drop the rest.
    private _uniqueByNewsId(items: NewsItemInterface[]): NewsItemInterface[] {
        const seen = new Set<number>();
        return items.filter(i => !seen.has(i.news_id) && seen.add(i.news_id));
    }

    private _adjustedLastUpdated(): string {
        if (!this.lastUpdated) return '';
        // Append Z so arithmetic is done in UTC, avoiding local-timezone drift.
        // The result is the stored Swedish-format timestamp minus exactly 1 second.
        const d = new Date(this.lastUpdated.replace(' ', 'T') + 'Z');
        d.setSeconds(d.getSeconds() - 1);
        return d.toISOString().replace('T', ' ').substring(0, 19);
    }

    private _toggleSignalR(start: boolean): void {
        if (start) {
            if (this._signalRSub) return;
            this._signalRSub = this._signalR
                .listenToEvent<string>(signalRConfig.incomingEvents.newsIntake)
                // auditTime, not debounce: a steady stream of events must not hold refreshes back.
                .pipe(auditTime(500))
                .subscribe(() => this.refreshNews());
        } else {
            this._signalRSub?.unsubscribe();
            this._signalRSub = null;
        }
    }

    private _togglePolling(start: boolean): void {
        if (start) {
            if (this._pollingSub) return;
            this._pollingSub = interval(this._pollInterval).subscribe(() => this.refreshNews());
        } else {
            this._pollingSub?.unsubscribe();
            this._pollingSub = null;
        }
    }

    private _buildSummaryPayload(summary: NewsSummaryInterface): NewsSummaryOutputInterface {
        const user = this._user.currentUserData$.getValue();
        const details = this.currentDetails;
        const payload: NewsSummaryOutputInterface = { ...summary } as any;
        payload.profile = summary.profile || user.profile;
        payload.user_id = user.user_id;
        payload.doc_id = summary.doc_id || details?.main_document?.doc_id || 0;
        if (summary.companies?.length) {
            (payload as any).companies = summary.companies.map(c => c.company_id);
        }
        if (payload.text?.length) {
            payload.text = payload.text.replaceAll('&nbsp;', ' ').replace(/\s{2,}/g, ' ');
        }
        if (summary.type === NewsSummaryTypeEnum.AI) {
            payload.type = NewsSummaryTypeEnum.EDITED;
        }
        return payload;
    }

    private _setInitialCounts(items: NewsItemInterface[]): void {
        this.freshNewsCount.edited_news_items = items.filter(i => i.has_summary_prediction && !i.has_summary_published).length;
        this.freshNewsCount.published_news_items = items.filter(i => i.has_summary_published).length;
    }

    private _toTimestamp(date: string | Date): string {
        if (!date) return '';
        if (typeof date === 'string') return date.replace('T', ' ').substring(0, 19);
        return (date as Date).toISOString().replace('T', ' ').substring(0, 19);
    }

    private _latestDate(items: NewsItemInterface[]): string {
        return items.reduce((latest, item) => {
            const d = String(item.updated_at).replace('T', ' ').substring(0, 19);
            return d > latest ? d : latest;
        }, '');
    }
}
